import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Users,
  Plus,
  Trash2,
  Heart,
  Baby,
  User as UserIcon,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const KEY = "silentscan_family_v1";

const DEFAULT = {
  active: null,
  profiles: [],
};

const RELATIONS = [
  "Self", "Spouse", "Parent", "Sibling", "Child", "Grandparent", "Guardian", "Other",
];

const load = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : DEFAULT;
  } catch {
    return DEFAULT;
  }
};
const save = (v) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch (e) {
    // storage full
  }
};

const newProfile = () => ({
  id: `f-${Math.random().toString(36).slice(2, 10)}`,
  name: "",
  relation: "Self",
  dob: "",
  blood_group: "",
  allergies: "",
  medications: "",
  conditions: "",
  emergency_phone: "",
});

const ageOf = (dob) => {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d)) return "";
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
};

export default function Family() {
  const [state, setState] = useState(() => load());
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const patchAll = (v) => {
    setState(v);
    save(v);
  };

  const addProfile = () => {
    const p = newProfile();
    const next = {
      ...state,
      profiles: [...state.profiles, p],
      active: state.active || p.id,
    };
    patchAll(next);
    setOpenId(p.id);
  };

  const updateProfile = (id, patch) => {
    const next = {
      ...state,
      profiles: state.profiles.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    };
    patchAll(next);
  };

  const deleteProfile = (id) => {
    const remaining = state.profiles.filter((p) => p.id !== id);
    const next = {
      ...state,
      profiles: remaining,
      active: state.active === id ? remaining[0]?.id || null : state.active,
    };
    patchAll(next);
    toast.success("Profile removed");
    if (openId === id) setOpenId(null);
  };

  const setActive = (id) => {
    patchAll({ ...state, active: id });
  };

  const active = state.profiles.find((p) => p.id === state.active);
  const editing = state.profiles.find((p) => p.id === openId);

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 10% 0%, rgba(52,211,153,0.14) 0%, transparent 60%), radial-gradient(45% 30% at 95% 20%, rgba(0,229,255,0.15) 0%, transparent 60%)",
        }}
      />
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="family-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <Link
            to="/dashboard"
            className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5"
          >
            Dashboard
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-12">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-emerald-300/80">
            Chapter 12 · Family
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
            Everyone at home,{" "}
            <span className="italic text-emerald-300">on one card.</span>
          </h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            Keep the essential health facts of the people you love in one
            place. Nothing leaves your device.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <button
            data-testid="family-add"
            onClick={addProfile}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-400 text-black font-medium px-5 py-2.5 text-sm hover:bg-emerald-300"
          >
            <Plus className="w-4 h-4" />
            Add family member
          </button>
          {active && (
            <div className="text-xs text-zinc-400">
              Active profile:{" "}
              <span className="text-cyan-300 font-medium">{active.name || "—"}</span>
            </div>
          )}
        </div>

        {state.profiles.length === 0 ? (
          <div
            data-testid="family-empty"
            className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center"
          >
            <Users className="w-8 h-8 text-cyan-300 mx-auto" />
            <div className="mt-3 font-display text-lg font-semibold">
              No profiles yet
            </div>
            <p className="mt-1 text-sm text-zinc-400 max-w-md mx-auto">
              Add yourself first, then any family members you look after.
            </p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {state.profiles.map((p) => (
              <FamilyCard
                key={p.id}
                profile={p}
                isActive={p.id === state.active}
                onOpen={() => setOpenId(p.id)}
                onDelete={() => deleteProfile(p.id)}
                onSetActive={() => setActive(p.id)}
              />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center p-3 sm:p-6"
            onClick={() => setOpenId(null)}
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="family-edit-modal"
              className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0A0A14] p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
                  Edit profile
                </div>
                <button
                  data-testid="family-close-modal"
                  onClick={() => setOpenId(null)}
                  className="text-xs text-zinc-400 hover:text-white"
                >
                  Done
                </button>
              </div>
              <FamilyForm
                profile={editing}
                onChange={(patch) => updateProfile(editing.id, patch)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const relationIcon = (rel) => {
  if (rel === "Child") return Baby;
  if (rel === "Parent" || rel === "Grandparent") return Heart;
  return UserIcon;
};

const FamilyCard = ({ profile, isActive, onOpen, onDelete, onSetActive }) => {
  const Icon = relationIcon(profile.relation);
  const age = ageOf(profile.dob);
  return (
    <motion.div
      data-testid={`family-card-${profile.id}`}
      whileHover={{ y: -3 }}
      className={`rounded-3xl border p-5 transition-colors ${
        isActive
          ? "border-cyan-400/50 bg-cyan-400/5"
          : "border-white/10 bg-white/[0.03] hover:bg-white/[0.05]"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-400/20 to-emerald-400/10 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-cyan-300" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-semibold text-white truncate">
              {profile.name || "Unnamed"}
            </div>
            <div className="text-xs text-zinc-400">
              {profile.relation}
              {age !== "" && ` · age ${age}`}
              {profile.blood_group && ` · ${profile.blood_group}`}
            </div>
          </div>
        </div>
        <button
          data-testid={`family-delete-${profile.id}`}
          onClick={onDelete}
          className="text-zinc-500 hover:text-red-400"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-4 flex flex-col gap-1 text-xs text-zinc-300">
        {profile.allergies && (
          <div className="truncate">
            <span className="text-red-300 font-medium">Allergies:</span> {profile.allergies}
          </div>
        )}
        {profile.medications && (
          <div className="truncate">
            <span className="text-amber-300 font-medium">Meds:</span> {profile.medications}
          </div>
        )}
        {profile.conditions && (
          <div className="truncate">
            <span className="text-cyan-300 font-medium">Conditions:</span> {profile.conditions}
          </div>
        )}
      </div>
      <div className="mt-5 flex gap-2">
        <button
          data-testid={`family-edit-${profile.id}`}
          onClick={onOpen}
          className="flex-1 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs text-white"
        >
          Edit
        </button>
        {!isActive && (
          <button
            data-testid={`family-activate-${profile.id}`}
            onClick={onSetActive}
            className="flex-1 rounded-full bg-cyan-500 text-black text-xs font-medium px-3 py-1.5 hover:bg-cyan-400"
          >
            <Sparkles className="w-3 h-3 inline mr-1" />
            Set active
          </button>
        )}
      </div>
    </motion.div>
  );
};

const FamilyForm = ({ profile, onChange }) => (
  <div className="flex flex-col gap-3">
    <Field label="Name" testId="family-form-name">
      <input
        value={profile.name}
        onChange={(e) => onChange({ name: e.target.value })}
        className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
      />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Relation" testId="family-form-relation">
        <select
          value={profile.relation}
          onChange={(e) => onChange({ relation: e.target.value })}
          className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
        >
          {RELATIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Date of birth" testId="family-form-dob">
        <input
          type="date"
          value={profile.dob}
          onChange={(e) => onChange({ dob: e.target.value })}
          className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
        />
      </Field>
      <Field label="Blood group" testId="family-form-blood">
        <input
          value={profile.blood_group}
          onChange={(e) => onChange({ blood_group: e.target.value.toUpperCase() })}
          placeholder="e.g. B+"
          className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
        />
      </Field>
      <Field label="Emergency phone" testId="family-form-phone">
        <input
          value={profile.emergency_phone}
          onChange={(e) => onChange({ emergency_phone: e.target.value })}
          placeholder="+91…"
          className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
        />
      </Field>
    </div>
    <Field label="Allergies" testId="family-form-allergies">
      <input
        value={profile.allergies}
        onChange={(e) => onChange({ allergies: e.target.value })}
        placeholder="peanuts, penicillin…"
        className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
      />
    </Field>
    <Field label="Current medications" testId="family-form-meds">
      <input
        value={profile.medications}
        onChange={(e) => onChange({ medications: e.target.value })}
        className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
      />
    </Field>
    <Field label="Chronic conditions" testId="family-form-conditions">
      <input
        value={profile.conditions}
        onChange={(e) => onChange({ conditions: e.target.value })}
        className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
      />
    </Field>
  </div>
);

const Field = ({ label, testId, children }) => (
  <label data-testid={testId} className="block">
    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
      {label}
    </span>
    {children}
  </label>
);
